// Primitives.jsx — atoms for the Klaren warm editorial site
import React, { useState, useEffect, useRef, useId } from "react";

export function Icon({ name, weight = "light", size = 20, color, style, ...rest }) {
  return (
    <i
      className={`ph-${weight} ph-${name}`}
      style={{ fontSize: size, color, lineHeight: 1, display: "inline-flex", ...style }}
      {...rest}
    />
  );
}

export function Btn({ children, variant = "solid", size = "md", onClick, icon, iconLeft, style, testId, full }) {
  const [h, setH] = useState(false);
  const pad = size === "sm" ? "11px 20px" : size === "lg" ? "16px 32px" : "13px 26px";
  const fs = size === "sm" ? 12.5 : size === "lg" ? 14.5 : 13.5;
  const base = {
    fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: fs, letterSpacing: ".03em",
    borderRadius: 999, padding: pad, border: "1px solid transparent", cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9,
    transition: "all .4s var(--ease)", whiteSpace: "nowrap", width: full ? "100%" : "auto", ...style,
  };
  const v = {
    solid: { background: h ? "#3a352f" : "var(--cta)", color: "var(--cta-ink)", boxShadow: h ? "0 14px 30px -14px rgba(39,35,32,.6)" : "0 0 0 rgba(0,0,0,0)", transform: h ? "translateY(-1px)" : "none" },
    outline: { background: h ? "rgba(39,35,32,.05)" : "transparent", color: "var(--ink)", borderColor: h ? "var(--ink)" : "var(--line)" },
    light: { background: h ? "#fff" : "rgba(255,255,255,.92)", color: "#272320", boxShadow: h ? "0 16px 34px -16px rgba(0,0,0,.5)" : "0 0 0 rgba(0,0,0,0)", transform: h ? "translateY(-1px)" : "none" },
    ghostLight: { background: h ? "rgba(255,255,255,.14)" : "transparent", color: "#fff", borderColor: "rgba(255,255,255,.55)" },
    ghost: { background: "transparent", color: h ? "var(--ink)" : "var(--ink-2)", padding: "8px 2px" },
  };
  return (
    <button data-hover data-testid={testId} style={{ ...base, ...v[variant] }} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
      {iconLeft && <Icon name={iconLeft} size={fs + 4} />}{children}{icon && <Icon name={icon} size={fs + 4} style={{ transform: h ? "translateX(3px)" : "none", transition: "transform .4s var(--ease)" }} />}
    </button>
  );
}

export function Display({ children, size = 64, style, as = "span" }) {
  const Tag = as;
  return <Tag className="k-display" style={{ fontSize: size, display: "block", ...style }}>{children}</Tag>;
}

export function Eyebrow({ children, style }) {
  return <span className="k-eye" style={style}>{children}</span>;
}

// reveal-on-scroll via IntersectionObserver (premium, settles crisp)
export function Reveal({ children, delay = 0, y = 26, style, once = true }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { el.classList.add("in"); return; }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add("in"); if (once) io.unobserve(e.target); }
          else if (!once) e.target.classList.remove("in");
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once]);
  return (
    <div ref={ref} className="k-reveal" style={{ transitionDelay: `${delay}s`, "--k-y": `${y}px`, ...style }}>
      {children}
    </div>
  );
}

// brand mark — an animated drop of liquid (water flows inside)
export function Mark({ color = "var(--ink)", size = 22, animated = true }) {
  const raw = useId();
  const uid = raw.replace(/[:]/g, "");
  const clip = `kd${uid}`;
  const grad = `kg${uid}`;
  const h = size * 1.12;
  const dropPath = "M12 2 C12 2 4 11 4 17 a8 8 0 0 0 16 0 C20 11 12 2 12 2 Z";
  return (
    <span style={{ position: "relative", width: size, height: h, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={size} height={h} viewBox="0 0 24 27" fill="none" aria-hidden="true">
        <defs>
          <clipPath id={clip}><path d={dropPath} /></clipPath>
          <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f1f4f7" />
            <stop offset="0.5" stopColor="#c2cad1" />
            <stop offset="1" stopColor="#8d96a0" />
          </linearGradient>
        </defs>
        <g clipPath={`url(#${clip})`}>
          <rect x="0" y="0" width="24" height="27" fill={`url(#${grad})`} opacity="0.16" />
          <g className={animated ? "k-drop-fill" : undefined}>
            <path className={animated ? "k-drop-wave" : undefined} d="M-12 16 q6 -3 12 0 t12 0 t12 0 t12 0 t12 0 V27 H-12 Z" fill={`url(#${grad})`} />
          </g>
        </g>
        <path d={dropPath} fill="none" stroke={color} strokeWidth="1.4" />
        <ellipse cx="9.4" cy="12.4" rx="1.5" ry="2.3" fill="#ffffff" opacity="0.55" />
      </svg>
    </span>
  );
}

export function Wordmark({ color = "var(--ink)", size = 24 }) {
  return (
    <span style={{ fontFamily: "var(--font-display)", fontWeight: 500, fontSize: size, letterSpacing: ".01em", color, lineHeight: 1 }}>Klaren</span>
  );
}

export function Loader({ label }) {
  return (
    <div className="k-loader-wrap">
      <div style={{ display: "flex", gap: 9 }}>
        <span className="k-loader-dot" /><span className="k-loader-dot" /><span className="k-loader-dot" />
      </div>
      {label && <span className="k-eye">{label}</span>}
    </div>
  );
}
